import React from "react"

import { format } from "date-fns"
import { CalendarDays, Clock, MapPin } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"

interface TimeSlot {
  time: string
  fullDate: Date
}

interface DateTimeSelectorProps {
  availableDates: Date[]
  selectedDate: Date | undefined
  onDateSelect: (date: Date | undefined) => void
  onTimeSelect: (fullDate: Date) => void
  title: string
  locationText: string
}

const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
  availableDates,
  selectedDate,
  onDateSelect,
  onTimeSelect,
  // Callers pass a title but this component never renders one - the heading is
  // currently the caller's responsibility.
  title: _title,
  locationText,
}) => {
  const isDateAvailable = (date: Date) => {
    return availableDates.some(
      (availableDate) =>
        availableDate.getDate() === date.getDate() &&
        availableDate.getMonth() === date.getMonth() &&
        availableDate.getFullYear() === date.getFullYear(),
    )
  }

  const availableTimesForDate = (date: Date): TimeSlot[] => {
    if (!date) return []
    return availableDates
      .filter(
        (availableDate) =>
          availableDate.getDate() === date.getDate() &&
          availableDate.getMonth() === date.getMonth() &&
          availableDate.getFullYear() === date.getFullYear(),
      )
      .map((d) => ({
        time: format(d, "h:mm a"),
        fullDate: d,
      }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Calendar Section */}
        <Card className="shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarDays className="h-5 w-5 text-honeycomb" />
              Choose Your Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                weekStartsOn={1}
                selected={selectedDate}
                onSelect={onDateSelect}
                disabled={(date) => !isDateAvailable(date) || date < new Date()}
                className="rounded-md border-0"
              />
            </div>
          </CardContent>
        </Card>

        {/* Time Selection */}
        <Card className="shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-honeycomb" />
              Available Times
            </CardTitle>
          </CardHeader>
          <CardContent className="min-h-[300px]">
            {selectedDate ? (
              availableTimesForDate(selectedDate).length > 0 ? (
                <div className="space-y-3">
                  <p className="mb-4 text-sm text-gray-600">
                    {format(selectedDate, "EEEE, MMMM d, yyyy")}
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {availableTimesForDate(selectedDate).map((timeSlot, index) => (
                      <Button
                        key={index}
                        type="button"
                        variant={
                          timeSlot.fullDate.getTime() === selectedDate?.getTime()
                            ? "default"
                            : "outline"
                        }
                        className={cn(
                          "h-12 w-full justify-start text-left",
                          timeSlot.fullDate.getTime() === selectedDate?.getTime()
                            ? "bg-honeycomb text-white hover:bg-amber-500"
                            : "hover:bg-gray-50",
                        )}
                        onClick={() => onTimeSelect(timeSlot.fullDate)}
                      >
                        <Clock className="mr-2 h-4 w-4" />
                        {timeSlot.time}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <Clock className="mb-3 h-12 w-12 text-gray-300" />
                  <p className="text-gray-500">No times available for this date</p>
                </div>
              )
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <CalendarDays className="mb-3 h-12 w-12 text-gray-300" />
                <p className="text-gray-500">Please select a date first</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Summary */}
      {selectedDate &&
        availableTimesForDate(selectedDate).some(
          (t) => t.fullDate.getTime() === selectedDate.getTime(),
        ) && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <Clock className="h-4 w-4 text-honeycomb" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-amber-900">Selected Slot</h3>
                  <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                    <div>
                      <span className="font-medium text-amber-800">Date & Time:</span>
                      <p className="text-amber-700">
                        {format(selectedDate, "EEEE, MMMM d, yyyy")} at{" "}
                        {format(selectedDate, "h:mm a")}
                      </p>
                    </div>
                    <div>
                      <span className="flex items-center gap-1 font-medium text-amber-800">
                        <MapPin className="h-3 w-3" />
                        Location:
                      </span>
                      <p className="text-amber-700">{locationText}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  )
}

export default DateTimeSelector
